import { HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { combineLatest, filter, Observable, tap } from 'rxjs';
import { distinctUntilChanged, map, shareReplay, switchMap, withLatestFrom } from 'rxjs/operators';
import { BildungsangebotPage } from '../model/bildungsangebot-page';
import { BildungsangebotTerminDetail } from '../model';
import { AccumulatedBildungsangeboteService } from './accumulatedBildungsangebote.service';
import { CombinedFacettenUrlService } from './facetten/combined-facetten-url.services';
import { ErweiterteSucheService } from './filter/erweiterte-suche.service';
import { BildungszielService } from './suchseite/bildungsziel.service';
import { CorrectionService } from './suchseite/correction.service';
import { RelaxationService } from './suchseite/relaxation.service';

import { Termin } from '../model';
import { BildungsbereichService } from './state/bildungsbereich.service';
import { SortableColumn, SortDirection } from './state/termin-tabelle-sorting.service';
import { BerufeService } from './suchseite/berufe.service';
import { UrlService } from './state/url.service';
import { OrtService } from './suchseite/ort.service';
import { SuchwortService } from './suchseite/suchwort.service';
import { PaginationService } from './suchseite/pagination.service';
import {
  BildungsangebotBasisFilterParameter,
  BildungsangebotClient,
  createBasicHttpParamsFor
} from './integration/bildungsangebot.client';

/**
 * Bündelt alle Parameter reaktiv und ruft die zugehörigen Seiten vom Backend ab und stellt
 * diese als Observable bereit.
 */
@Injectable({
  providedIn: 'root'
})
export class BildungsangeboteService {
  private readonly urlService = inject(UrlService);
  private readonly ortService = inject(OrtService);
  private readonly combinedFacettenUrlService = inject(CombinedFacettenUrlService);
  private readonly suchwortService = inject(SuchwortService);
  private readonly bildungsangebotClient = inject(BildungsangebotClient);
  private readonly bildungszielService = inject(BildungszielService);
  private readonly correctionService = inject(CorrectionService);
  private readonly relaxationService = inject(RelaxationService);
  private readonly accumulatedBildungsangeboteService = inject(AccumulatedBildungsangeboteService);
  private readonly paginationService = inject(PaginationService);
  private readonly erweiterteSucheService = inject(ErweiterteSucheService);
  private readonly berufeService = inject(BerufeService);
  private readonly bildungsbereichService = inject(BildungsbereichService);

  private lastParams = '';

  /**
   * Die nummerische Angebotsseite, die aktuell auf der Ergebnisseite angezeigt wird, bzw. auf der sich das
   * Angebot der dargestellten Detailseite befindet.
   */
  private readonly aktuelleSeite$: Observable<number> = this.urlService
    .getQueryParameterValue(UrlService.AKTUELLE_SEITE)
    .pipe(
      map((val) => {
        const page = Number.parseInt(val!);
        return !isNaN(page) && page > 0 ? page : 0;
      }),
      distinctUntilChanged(),
      withLatestFrom(this.paginationService.loadedPages$),
      map(([page]) => page),
      distinctUntilChanged()
    );

  /**
   * Aktuelle Seite mit den Bildungsangebot-Terminen und den zugehörigen Aggregationen
   * der Suche zur Darstellung der Facetten.
   */
  public bildungsAngebotePage$: Observable<BildungsangebotPage> = combineLatest([
    this.aktuelleSeite$,
    this.erweiterteSucheService.erweiterteSucheActive$,
    this.paginationService.loadedPages$.pipe(
      distinctUntilChanged((a, b) => {
        // Emitte nur einen neuen Wert, wenn sich das vorherige Array unterscheidet,
        // umd spätere Seiteneffekte zu vermeiden
        if (a.length !== b.length) return false;
        return a.every((value, index) => value === b[index]);
      })
    ),
    this.urlService.getQueryParameterString()
  ]).pipe(
    filter(([seite, _, loadedPages, param]) => {
      // Lade nur eine neue Seite wenn sie noch nicht geladen wurde
      const filteredParam = param
        .split('&')
        .filter((p) => !p.startsWith('angebot'))
        .join('&');
      const shouldFilter =
        !loadedPages.includes(seite) || (seite == 0 && filteredParam != this.lastParams);
      this.lastParams = filteredParam;
      return shouldFilter;
    }),
    map(([seite]) => seite),
    switchMap((page) => this.bildungsAngebotTermineByPage$(page)),
    tap((page) => {
      this.correctionService.setCorrection(page?.korrekturVorschlag!);
      this.relaxationService.setRemovedToken(page?.removedToken!);
      this.accumulatedBildungsangeboteService.addAngebotePage(page);
    }),
    shareReplay(1)
  );

  public fetchTermineForAngebot: (
    id: number,
    page: number,
    sort: SortableColumn,
    sortOrder: SortDirection
  ) => Observable<Termin[]> = (id, page, sort, sortOrder) => {
    //Page -1 da wir mit "Page 0" arbeiten...
    return this.prepareHttpParams(page - 1).pipe(
      map((params) => params.set('terminSortierung', sort)),
      map((params) => params.set('terminSortierungsOrder', sortOrder)),
      switchMap((httpParams) => this.bildungsangebotClient.getTermineForAngebot(id, httpParams))
    );
  };

  private readonly bildungsAngebotTermineByPage$: (
    page: number
  ) => Observable<BildungsangebotPage> = (page) =>
    this.prepareHttpParams(page).pipe(
      switchMap((httpParams) => this.bildungsangebotClient.getBildungsangeboteByDkz(httpParams))
    );

  private prepareHttpParams(page: number): Observable<HttpParams> {
    return combineLatest([
      this.suchwortService.showRequiredInputState$,
      this.suchwortService.suchwort$,
      this.ortService.umkreisParameter$,
      this.combinedFacettenUrlService.combinedFacetteParameters$,
      this.bildungszielService.dkzIds$,
      this.ortService.ortsunabhaengig$,
      this.correctionService.correctionState$,
      this.berufeService.ids$,
      this.bildungsbereichService.bildungsbereich$
    ]).pipe(
      filter(([showRequiredInputState]) => {
        return !showRequiredInputState;
      }),
      map(
        ([
          _,
          suchwort,
          umkreis,
          facettenParameter,
          dkzIds,
          ortsunabhaengig,
          correctionState,
          berufe,
          bildungsbereich
        ]) => {
          const basisFilterParameter: BildungsangebotBasisFilterParameter = {
            dkzIds,
            suchwort,
            umkreis,
            page,
            ortsunabhaengig,
            correctionState,
            berufe,
            bildungsbereich
          };
          let httpParams = createBasicHttpParamsFor(basisFilterParameter);
          // Füge alle Parameter der Facetten hinzu
          httpParams = facettenParameter
            .filter(({ ids }) => ids.length > 0)
            .reduce(
              (httpParams, { param, ids }) =>
                httpParams.set(param, ids.join(UrlService.VALUE_SEPARATOR)),
              httpParams
            );
          return httpParams;
        }
      )
    );
  }

  public getAktuellenTermin$(id: string): Observable<BildungsangebotTerminDetail> {
    return this.bildungsangebotClient.getTerminById(id);
  }

  public getThemenfelderCounts$(
    themenfelder: string[],
    isUte: boolean
  ): Observable<{ [key: string]: number }> {
    return this.bildungsangebotClient.getCountsForSuchworte(themenfelder, isUte);
  }
}
